import React, { useState, useEffect, useMemo } from 'react';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import { Stack } from '@mui/material';
import { PermissionArea } from '@cloudcost/shared-types/users';
import { getUser } from '@/lib/common';
import { Loader } from '@/components/loader/loader';
import AnnualBudgetConsumptionChart from '../charts/annual-budget-consumption-chart';
import MonthlyBudgetConsumptionChart from '../charts/monthly-budget-consumption-chart';
import { AppCard } from '@/components/app-card/app-card';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';

import { useSystems } from './hooks/useSystems';
import { SystemData } from '../calculations';
import { OwnedSystemsTabs } from './systems/owned-systems-tabs';
import { hasUserAccessTo } from '@/lib/permissions';
import { BudgetChangeModal, BudgetChangeData } from './budget-change-modal';
import { CurrencyToggle } from '@/components/currency-toggle/CurrencyToggle';
import { useActionCenterViewContext } from '@/contexts/action-center-view-context';

interface SystemsBudgetProps {
  initialSystemId?: number;
  onSystemNotFound?: (systemId: number, departmentId?: number) => void;
  isActive?: boolean; // Whether this tab is currently active
}

export default function SystemsBudget({ initialSystemId, onSystemNotFound, isActive = true }: SystemsBudgetProps = {}) {
  const [budgetRequestModalOpen, setBudgetRequestModalOpen] = useState(false);
  const [selectedSystemForRequest, setSelectedSystemForRequest] =
    useState<SystemData | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [departmentOwnerName, setDepartmentOwnerName] = useState<string>('');
  const [departmentOwnerId, setDepartmentOwnerId] = useState<number>();
  const [loading, setLoading] = useState(true);
  const [departmentBudgets, setDepartmentBudgets] = useState<
    Map<number, { value: number; currency: string; name: string }>
  >(new Map());

  const user = getUser();
  const { clearSelection } = useActionCenterViewContext();

  const [tabValue, setTabValue] = React.useState(0);
  const handleTabChange = (_event: any, newValue: any) => {
    setTabValue(newValue);
    // Clear selection when user manually changes system tab
    clearSelection();
  };

  const canViewSystemList =
    hasUserAccessTo(PermissionArea.SYSTEM_VIEW_ALL) ||
    hasUserAccessTo(PermissionArea.SYSTEM_VIEW_DEPARTMENT) ||
    hasUserAccessTo(PermissionArea.SYSTEM_VIEW_OWNED);

  const { items, loading: systemsLoading, refreshData } = useSystems();

  const getCurrentTabSystem = (): SystemData | undefined => {
    return ownedSystemList[tabValue];
  };

  // Get the system ID to use for charts - prefer initialSystemId if provided
  // This fixes the race condition where charts render before tabValue is set from initialSystemId
  const getSystemIdForCharts = (): number | undefined => {
    const currentSystem = getCurrentTabSystem();
    const currentSystemId = currentSystem?.id;
    
    // If initialSystemId is provided and exists in the list, use it directly
    // This ensures charts use the correct system ID even before tabValue is fully updated
    if (initialSystemId && ownedSystemList.some(sys => sys.id === initialSystemId)) {
      // Only use initialSystemId if current tab system doesn't match yet (race condition)
      // Once tabValue is set correctly, currentSystem.id will match initialSystemId
      if (!currentSystem || currentSystem.id !== initialSystemId) {
        return initialSystemId;
      }
    }
    // Otherwise use the current tab system
    return currentSystemId;
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const [usersData, departmentsData, departmentBudgetsData] =
          await Promise.all([
            fetchWrapper.get(urls.DevOps.getUsersFilter),
            fetchWrapper.get(urls.DevOps.getFilterDepartments),
            fetchWrapper.get(urls.DevOps.getDepartments),
          ]);

        // department owner - find departments owned by the user
        if (departmentsData.length > 0) {
          const userOwnedDepartments = departmentsData.filter(
            (dept: any) => dept.id_owner === user?.id,
          );

          if (userOwnedDepartments.length > 0 && usersData.length > 0) {
            // Use the first owned department for department owner info
            const userDepartment = userOwnedDepartments[0];
            if (userDepartment.id_owner) {
              const departmentOwner = usersData.find(
                (u: any) => u.id === userDepartment.id_owner,
              );
              if (departmentOwner) {
                setDepartmentOwnerName(
                  `${departmentOwner.firstName} ${departmentOwner.lastName}`,
                );
                setDepartmentOwnerId(departmentOwner.id);
              }
            }
          }
        }

        const deptBudgetsMap = new Map();
        departmentBudgetsData.forEach((dept: any) => {
          const yearly =
            dept.original_currency_value != null
              ? Number(dept.original_currency_value)
              : Number(dept.value);
          if (Number.isFinite(yearly) && yearly > 0 && dept.original_currency) {
            deptBudgetsMap.set(dept.id, {
              value: yearly,
              currency: dept.original_currency,
              name: dept.name,
            });
          }
        });
        setDepartmentBudgets(deptBudgetsMap);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const handleBudgetRequestSubmit = async (data: BudgetChangeData) => {
    if (!selectedSystemForRequest) return;

    try {
      await fetchWrapper.post(urls.DevOps.createSystemBudgetRequest, {
        systemId: selectedSystemForRequest.id,
        currency: selectedSystemForRequest.original_currency || 'USD',
        newBudgetValue: data.newAnnualBudget,
        reason: data.reason,
        requesterId: user?.id,
        approverId: departmentOwnerId,
      });

      refreshData();
      setRefreshTrigger((prev) => prev + 1);
      setBudgetRequestModalOpen(false);
      setSelectedSystemForRequest(null);
    } catch (error) {
      console.error('Error creating system budget request:', error);
    }
  };

  const ownedSystemList = useMemo(() => {
    // For owned systems, we should use the original items directly
    // regardless of department filtering, as long as user has permission to view systems
    if (!canViewSystemList) {
      return [];
    }

    // Use items directly instead of filteredItems to avoid department dependency
    return items.filter((system) => {
      return system.owner_id === user?.id;
    });
  }, [items, user, canViewSystemList]);

  // Track if we've already processed initialSystemId to prevent re-triggering fallback
  const hasProcessedInitialSystemId = React.useRef(false);
  const lastProcessedSystemId = React.useRef<number | undefined>(undefined);

  // Deep link: Select system sub-tab when initialSystemId is provided
  useEffect(() => {
    // Only process if this tab is active - prevents fallback from triggering when on departments tab
    if (!isActive) {
      return;
    }
    
    // Wait for systems to finish loading before checking
    if (systemsLoading || loading) {
      return;
    }
    
    if (initialSystemId) {
      // If this is a new initialSystemId (different from last processed), reset the flag
      if (lastProcessedSystemId.current !== initialSystemId) {
        hasProcessedInitialSystemId.current = false;
        lastProcessedSystemId.current = initialSystemId;
      }
      
      if (ownedSystemList.length > 0) {
        const idx = ownedSystemList.findIndex(system => system.id === initialSystemId);
        if (idx !== -1) {
          setTabValue(idx);
          hasProcessedInitialSystemId.current = true;
          
          // Scroll to system section after tab is selected
          const scrollToSystem = () => {
            const systemElement = document.querySelector('[data-system-section]');
            if (systemElement) {
              const elementPosition = systemElement.getBoundingClientRect().top;
              const offsetPosition = window.pageYOffset + elementPosition - 24;
              
              window.scrollTo({
                top: Math.max(offsetPosition, 0),
                behavior: 'smooth'
              });
            }
          };
          
          // Use requestAnimationFrame to ensure DOM is rendered, then scroll
          requestAnimationFrame(() => {
            setTimeout(scrollToSystem, 600);
          });
        } else {
          // System not found in owned systems - only trigger fallback once per unique systemId
          if (!hasProcessedInitialSystemId.current) {
            console.warn('[SystemsBudget] System not found in ownedSystemList (unexpected), triggering fallback to departments:', initialSystemId);
            hasProcessedInitialSystemId.current = true; // Mark as processed to prevent re-triggering
            if (onSystemNotFound) {
              // Find the system in all systems to get departmentId
              const system = items.find(s => s.id === initialSystemId);
              onSystemNotFound(initialSystemId, system?.department_id);
            }
          }
        }
      } else if (ownedSystemList.length === 0 && !systemsLoading && !loading) {
        // No owned systems but we have an initialSystemId - only trigger fallback once
        if (!hasProcessedInitialSystemId.current) {
          console.warn('[SystemsBudget] No owned systems but initialSystemId provided (unexpected), triggering fallback to departments');
          hasProcessedInitialSystemId.current = true; // Mark as processed to prevent re-triggering
          if (onSystemNotFound) {
            const system = items.find(s => s.id === initialSystemId);
            onSystemNotFound(initialSystemId, system?.department_id);
          }
        }
      }
    } else if (!initialSystemId) {
      // Reset flags when initialSystemId is cleared
      hasProcessedInitialSystemId.current = false;
      lastProcessedSystemId.current = undefined;
    }
  }, [initialSystemId, ownedSystemList, tabValue, systemsLoading, loading, items, onSystemNotFound, isActive]);

  if (loading || systemsLoading) {
    return <Loader />;
  }

  return (
    <Stack spacing={2}>
      {ownedSystemList.length > 0 && (
        <>
          <div data-system-section>
            <OwnedSystemsTabs
              ownedSystemList={ownedSystemList}
              tabValue={tabValue}
              onTabChange={handleTabChange}
              getCurrentTabSystem={getCurrentTabSystem}
              onRequestBudgetChange={(system) => {
                setSelectedSystemForRequest(system);
                setBudgetRequestModalOpen(true);
              }}
            />
          </div>

          <AppCard
            className="card100"
            title="Annual Budget Consumption"
            icon={<BarChartOutlinedIcon color="primary" />}
            headerRight={<CurrencyToggle budgetCurrency={getCurrentTabSystem()?.original_currency || undefined} />}>
            <AnnualBudgetConsumptionChart
              budgetType="system"
              entityId={getSystemIdForCharts()}
              refreshTrigger={refreshTrigger}
              budgetCurrency={getCurrentTabSystem()?.original_currency || undefined}
            />
          </AppCard>

          <AppCard
            className="card100"
            title="Monthly Budget Consumption"
            icon={<BarChartOutlinedIcon color="primary" />}
            headerRight={<CurrencyToggle budgetCurrency={getCurrentTabSystem()?.original_currency || undefined} />}>
            <MonthlyBudgetConsumptionChart
              budgetType="system"
              entityId={getSystemIdForCharts()}
              refreshTrigger={refreshTrigger}
              budgetCurrency={getCurrentTabSystem()?.original_currency || undefined}
            />
          </AppCard>
        </>
      )}

      <BudgetChangeModal
        open={budgetRequestModalOpen}
        onClose={() => {
          setBudgetRequestModalOpen(false);
          setSelectedSystemForRequest(null);
        }}
        onSubmit={handleBudgetRequestSubmit}
        budgetType="system"
        currentBudget={selectedSystemForRequest?.value || 0}
        budgetCurrency={selectedSystemForRequest?.original_currency || ''}
        entityName={selectedSystemForRequest?.name}
        systemOwner={selectedSystemForRequest?.owner}
        departmentBudget={
          departmentBudgets.get(selectedSystemForRequest?.department_id || 0)
            ?.value
        }
        departmentsBudgetsSum={
          items
            .filter(
              (sys) =>
                sys.department_id === selectedSystemForRequest?.department_id,
            )
            .reduce((sum, sys) => sum + (sys.value || 0), 0) -
          (selectedSystemForRequest?.value || 0)
        }
        isRequest={true}
        approverName={departmentOwnerName}
      />
    </Stack>
  );
}
